import React from 'react';
import { createPortal } from 'react-dom';

const PlayerControls = ({
  availableSeasons,
  selectedSeasons,
  toggleSeason,
  selectedStat,
  setSelectedStat,
  showStatDropdown,
  setShowStatDropdown,
  groupedOdds,
  getBestOdds,
  showOverOdds,
  setShowOverOdds,
  handleLineChange,
  customLine,
  includeFinals,
  setIncludeFinals,
  setActiveFilters,
  onMarketClick
}) => {
  // Add state for year dropdown
  const [showYearDropdown, setShowYearDropdown] = React.useState(false);
  
  // Add click outside handler
  React.useEffect(() => {
    if (showYearDropdown) {
      const handleClickOutside = (event) => {
        if (!event.target.closest('.year-dropdown')) {
          setShowYearDropdown(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showYearDropdown]);

  return (
    <div className="flex flex-col w-full lg:w-auto lg:min-w-[400px] xl:min-w-[600px]">
      {/* Button Groups Container */}
      <div className="flex items-center gap-2 overflow-x-auto">
        {/* Finals Toggle */}
        <button
          onClick={() => {
            // Toggle finals filter
            if (includeFinals) {
              // If currently including finals, add excludeFinals filter
              setActiveFilters(prev => [...prev, { type: 'excludeFinals' }]);
            } else {
              // If currently excluding finals, remove excludeFinals filter
              setActiveFilters(prev => prev.filter(f => f.type !== 'excludeFinals'));
            }
            setIncludeFinals(!includeFinals);
          }}
          className={`px-2 py-1 rounded text-xs font-medium transition-colors flex-shrink-0 ${
            includeFinals
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
          }`}
        >
          {includeFinals ? 'w Finals' : 'w/o Finals'}
        </button>

        {/* Separator */}
        <div className="h-4 w-px bg-gray-200 dark:bg-gray-700 flex-shrink-0"></div>

        {/* Season buttons - Desktop view */}
        <div className="hidden sm:flex flex-shrink-0 overflow-x-auto scrollbar-hide">
          <div className="flex items-center gap-1 min-w-max">
            {availableSeasons.map(season => (
              <button
                key={season}
                onClick={() => toggleSeason(season)}
                className={`px-1.5 py-0.5 rounded text-xs font-medium transition-colors ${
                  selectedSeasons.includes(season)
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {season.toString().slice(-2)}'
              </button>
            ))}
          </div>
        </div>

        {/* Season dropdown - Mobile view */}
        <div className="sm:hidden relative">
          <button
            onClick={(e) => {
              const buttonRect = e.currentTarget.getBoundingClientRect();
              document.documentElement.style.setProperty('--dropdown-top', `${buttonRect.bottom + window.scrollY + 4}px`);
              document.documentElement.style.setProperty('--dropdown-left', `${buttonRect.left + window.scrollX}px`);
              setShowYearDropdown(!showYearDropdown);
            }}
            className="px-2 py-1 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 year-dropdown"
          >
            {selectedSeasons.length > 0 
              ? `${selectedSeasons.length} Year${selectedSeasons.length > 1 ? 's' : ''}`
              : 'Select Years'}
          </button>
          {showYearDropdown && createPortal(
            <div 
              className="year-dropdown fixed bg-white dark:bg-gray-800 rounded-md shadow-lg z-[9999] w-48"
              style={{
                top: 'var(--dropdown-top)',
                left: 'var(--dropdown-left)',
                maxHeight: '15rem',
                overflow: 'auto'
              }}
            >
              <div className="p-2">
                <div className="space-y-1">
                  {availableSeasons.map(season => (
                    <div
                      key={season}
                      onClick={() => toggleSeason(season)}
                      className="flex items-center px-2 py-1.5 rounded text-sm hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                    >
                      <div className={`w-4 h-4 border rounded mr-2 flex items-center justify-center ${
                        selectedSeasons.includes(season) 
                          ? 'bg-blue-600 border-blue-600' 
                          : 'border-gray-300 dark:border-gray-600'
                      }`}>
                        {selectedSeasons.includes(season) && (
                          <svg className="w-3 h-3 text-white" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      {season}
                    </div>
                  ))}
                </div>
              </div>
            </div>,
            document.body
          )}
        </div>

        {/* Separator */}
        <div className="h-4 w-px bg-gray-200 dark:bg-gray-700 flex-shrink-0"></div>
        {/* Stat Selection */}
        <div className="flex-1 overflow-x-auto scrollbar-hide">
          <div className="flex items-center gap-1 min-w-max">
            {[
              { value: 'tries', label: 'Tries' },
              { value: 'points', label: 'Points' },
              { value: 'performance_points', label: 'Player Performance' }
            ].map(stat => (
              <button
                key={stat.value}
                onClick={() => {
                  // When changing stats, it will reset the line to the first available market
                  // or the calculated average in the useEffect in PlayerProfile.js
                  setSelectedStat(stat.value);
                }}
                className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                  selectedStat === stat.value 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {stat.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Market Lines */}
      {Object.keys(groupedOdds).length > 0 && (
        <div className="mt-0.5 overflow-x-auto scrollbar-hide -mx-2 px-2">
          <div className="flex items-center gap-1 min-w-max">
            {Object.keys(groupedOdds)
              .sort((a, b) => {
                // Special handling for FIRST/LAST
                if (a === 'FIRST' || a === 'LAST') return -1;
                if (b === 'FIRST' || b === 'LAST') return 1;
                return parseFloat(a.replace(/\+/g, '')) - parseFloat(b.replace(/\+/g, ''));
              })
              .map(line => {
                const bestOver = getBestOdds(line, 'over');
                const bestUnder = getBestOdds(line, 'under');
                
                // Determine if this line is selected
                let isSelected = false;
                if (line === 'FIRST' || line === 'LAST') {
                  isSelected = customLine === 0.5;
                } else {
                  const endsWithPlus = line.includes('+');
                  const numericLine = parseFloat(line.replace(/\+/g, ''));
                  const adjustedLine = endsWithPlus ? Math.max(numericLine - 0.5, 0.5) : numericLine;
                  isSelected = customLine === adjustedLine;
                }
                
                const hasBothOdds = bestOver && bestUnder;
                
                // Skip lines that don't have the currently selected odds type
                // For lines with only one type, show that type regardless of showOverOdds
                if (!hasBothOdds) {
                  if (!bestOver && !bestUnder) return null;
                } else if ((showOverOdds && !bestOver) || (!showOverOdds && !bestUnder)) {
                  return null;
                }
                
                // Determine which odds to show
                const oddsToShow = hasBothOdds 
                  ? (showOverOdds ? bestOver : bestUnder)
                  : (bestOver || bestUnder);
                
                return (
                  <div 
                    key={line}
                    onClick={() => onMarketClick(line)}
                    className={`
                      border rounded-lg overflow-hidden cursor-pointer transition-colors w-[72px]
                      ${isSelected 
                        ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-600' 
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'}
                    `}
                  >
                    <div className="px-2 py-1">
                      <div className="text-sm font-medium text-gray-900 dark:text-white mb-0.5 flex items-center justify-between">
                        <span>{line}</span>
                        {hasBothOdds && (
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowOverOdds(!showOverOdds);
                            }}
                            className={`text-xs font-medium ${
                              isSelected 
                                ? 'text-blue-600 dark:text-blue-400' 
                                : 'text-gray-500 dark:text-gray-400'
                            } hover:text-blue-600 dark:hover:text-blue-400`}
                          >
                            {showOverOdds ? 'O' : 'U'}
                          </button>
                        )}
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          ${oddsToShow?.value}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayerControls; 